/**
 * Identicon: um símbolo gerado a partir de um texto, sempre o mesmo para o
 * mesmo texto — o avatar padrão do GitHub funciona assim.
 *
 * Escrito aqui em vez de instalado: as bibliotecas prontas escolhem as cores
 * sozinhas, e o DESIGN.md define paleta de tema único (sem verde, sem amarelo,
 * destaque em ciano). Gerando a figura localmente, as cores saem dos tokens do
 * tema e o resultado pertence ao sistema visual.
 *
 * Nada aqui vai para a rede nem para o banco: a figura é derivada do nome da
 * equipe no próprio navegador.
 */

/** FNV-1a de 32 bits: rápido, determinístico e bem distribuído. */
function hash(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

const GRID = 5;

/**
 * Metade esquerda do desenho, espelhada — a simetria é o que faz o identicon
 * parecer um emblema e não ruído.
 */
function cells(seed: number): boolean[][] {
  const metade = Math.ceil(GRID / 2);
  const grid: boolean[][] = [];
  let bits = seed;

  for (let y = 0; y < GRID; y++) {
    const linha: boolean[] = new Array<boolean>(GRID).fill(false);
    for (let x = 0; x < metade; x++) {
      bits = Math.imul(bits, 0x01000193) >>> 0;
      const preenchido = ((bits >>> (x + y)) & 1) === 1;
      linha[x] = preenchido;
      linha[GRID - 1 - x] = preenchido;
    }
    grid.push(linha);
  }
  return grid;
}

/**
 * Tons de destaque da paleta, por opacidade. Mantém o ciano do tema e evita
 * inventar matizes que o DESIGN.md proíbe.
 */
const OPACIDADES = [1, 0.85, 0.7, 0.55] as const;

export function Identicon({
  seed,
  className = "size-8",
  title,
}: {
  /** Texto de origem. Use algo estável: o id da equipe, não o nome editável. */
  seed: string;
  className?: string;
  title?: string;
}): React.JSX.Element {
  const h = hash(seed);
  const grid = cells(h);
  const opacidade = OPACIDADES[h % OPACIDADES.length] ?? 1;

  return (
    <svg
      viewBox={`0 0 ${GRID} ${GRID}`}
      className={`${className} shrink-0 rounded-lg bg-background text-accent`}
      role={title ? "img" : "presentation"}
      {...(title ? { "aria-label": title } : { "aria-hidden": true })}
    >
      {title && <title>{title}</title>}
      {grid.map((linha, y) =>
        linha.map((preenchido, x) =>
          preenchido ? (
            <rect
              key={`${x}-${y}`}
              x={x}
              y={y}
              width={1}
              height={1}
              fill="currentColor"
              opacity={opacidade}
            />
          ) : null,
        ),
      )}
    </svg>
  );
}
